<template>
  <div class="app-wrapper">
    <AppNav v-if="supabase.user.status !== 'Unknown' && !shouldHideSidebar" :user="supabase.user" />
    <div
      class="flex flex-col"
      :class="{
        'col-span-2': shouldHideSidebar
      }"
    >
      <Toast />
      <div>
        <RouterView v-slot="{ Component }">
          <Transition :name="transitionName" :disabled="isReducedMotion">
            <component
              class="p-4 app-container bg-white dark:bg-stone-950 text-stone-900 dark:text-stone-50 mx-auto relative"
              :class="{
                'full-screen': route.meta.fullScreen === true,
                'hide-sidebar': shouldHideSidebar
              }"
              :is="Component"
              :user="supabase.user.status === 'SignedIn' ? supabase.user.data : null"
              :key="route.path"
            />
          </Transition>
        </RouterView>
      </div>

      <Suspense>
        <DevtoolWrapper />
      </Suspense>
    </div>
  </div>
</template>

<script setup lang="ts">
import '@fontsource-variable/comfortaa'
import '@fontsource-variable/quicksand'
import { useSupabase } from './stores/supabase'
import { useNotifications } from './stores/notifications'
import { useTheme } from './stores/theme'
import { App } from '@capacitor/app'
import { useRouter } from 'vue-router'
import { useRoute } from 'vue-router'
import { computed, onMounted } from 'vue'
import { useVibrate } from '@/composables/useVibrate'
import { useI18n } from 'vue-i18n'
import Toast from '@/components/Toast.vue'
import AppNav from '@/components/AppNav.vue'
import { usePhotos } from './stores/photos'
import { useFriends } from './stores/friends'
import DevtoolWrapper from '@/components/DevtoolWrapper.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { vibrate } = useVibrate()
const supabase = useSupabase()
const notifications = useNotifications()
const theme = useTheme()
const photos = usePhotos()
const friends = useFriends()

const transitionName = computed(() => route.meta.transition as string)
const isReducedMotion = computed(() => theme.isReducedMotion)
const isDev = import.meta.env.DEV

onMounted(() => {
  // Initialize theme immediately to prevent flash
  theme.initializeTheme()
  friends.getMyFriends()
})

router.afterEach((to, from) => {
  const toDepth = to.path.split('/').length
  const fromDepth = from.path.split('/').length
  const toPosition = to.meta.position ?? -1
  const fromPosition = from.meta.position ?? 999

  if (toPosition !== -1 && fromPosition !== -1) {
    if (toPosition < fromPosition) {
      to.meta.transition = 'slide-left'
    } else if (toPosition > fromPosition) {
      to.meta.transition = 'slide-right'
    } else if (toDepth !== fromDepth) {
      to.meta.transition = toDepth < fromDepth ? 'slide-left' : 'slide-right'
    } else {
      to.meta.transition = ''
    }
  } else {
    to.meta.transition = toDepth < fromDepth ? 'slide-left' : 'slide-right'
  }
})

App.addListener('backButton', async () => {
  if (router.currentRoute.value.path !== '/') {
    router.back()
  } else {
    await App.exitApp()
  }
})

const shouldHideSidebar = computed(() => {
  if (route.meta.hideSidebar) {
    return (route.meta.hideSidebar as () => boolean)()
  } else return false
})
</script>

<style>
html {
  background: theme('colors.white');
  color: theme('colors.stone.900');

  &.dark {
    background: theme('colors.stone.950');
    color: theme('colors.stone.50');
  }
}

/* Disable transitions when reduced motion is enabled */
.motion-reduce * {
  transition-duration: 0.01ms !important;
  animation-duration: 0.01ms !important;
  animation-iteration-count: 1 !important;
}

.slide-right-enter-active,
.slide-right-leave-active,
.slide-left-enter-active,
.slide-left-leave-active {
  position: fixed;
  transition: none;
}

@media (max-width: 768px) {
  .slide-right-enter-active {
    transition: all 0.1s ease-out;
  }

  .slide-right-leave-active {
    transition: all 0.1s cubic-bezier(1, 0.5, 0.8, 1);
  }

  .slide-right-enter-from,
  .slide-left-leave-to {
    transform: translateX(50px);
    opacity: 0;
  }

  .slide-left-enter-active {
    transition: all 0.1s ease-out;
  }

  .slide-left-leave-active {
    transition: all 0.1s cubic-bezier(1, 0.5, 0.8, 1);
  }

  .slide-left-enter-from,
  .slide-right-leave-to {
    transform: translateX(-50px);
    opacity: 0;
  }
}

.router-link-exact-active {
  color: theme('colors.blue.500');

  & img {
    border-color: theme('colors.blue.500');
  }
}
</style>
